import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { SystemRole, OrgRole, isAdminRole, isSuperAdminRole } from '@/types/roles';
import { setUserContext } from '@/lib/sentry';

interface Profile {
  id: string;
  system_role: SystemRole;
  org_role?: OrgRole | null;
  full_name: string | null;
  business_name?: string | null;
  business_address?: string | null;
  business_email?: string | null;
  business_hours?: any | null;
  is_internal?: boolean | null;
  phone: string | null;
  email?: string | null;
  avatar_url: string | null;
  vendor_status: 'pending' | 'approved' | 'rejected' | null;
  has_vendor_management_access?: boolean;
  // Deprecated - conservé pour compatibilité temporaire
  role?: SystemRole;
  user_role?: 'admin' | 'moderator' | 'hr' | 'manager' | 'employee' | 'relay_operator' | null;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  loading: boolean;
  isEffectiveVendor: boolean;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  isHR: boolean;
  isVendor: boolean;
  isRelay: boolean;
  isClient: boolean;
  signUp: (email: string, password: string, fullName: string) => Promise<{ error: any }>;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  updateProfile: (updates: Partial<Profile>) => Promise<{ error: any }>;
  updateEmail: (newEmail: string) => Promise<{ error: any }>;
  updatePassword: (newPassword: string) => Promise<{ error: any }>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  profile: null,
  loading: true,
  isEffectiveVendor: false,
  isAdmin: false,
  isSuperAdmin: false,
  isHR: false,
  isVendor: false,
  isRelay: false,
  isClient: false,
  signUp: async () => ({ error: null }),
  signIn: async () => ({ error: null }),
  signOut: async () => {},
  updateProfile: async () => ({ error: null }),
  updateEmail: async () => ({ error: null }),
  updatePassword: async () => ({ error: null }),
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  // Helpers pour vérifier les rôles
  const isAdmin = profile ? isAdminRole(profile.system_role) : false;
  const isSuperAdmin = profile ? isSuperAdminRole(profile.system_role) : false;
  const isHR = profile?.system_role === 'hr';
  const isVendor = profile?.system_role === 'vendor';
  const isRelay = profile?.system_role === 'relay';
  const isClient = profile?.system_role === 'client';

  const isEffectiveVendor = profile ? (
    profile.system_role === 'admin' || 
    profile.system_role === 'super_admin' || 
    (profile.system_role === 'vendor' && profile.vendor_status === 'approved') ||
    profile.has_vendor_management_access === true
  ) : false;

  const fetchProfile = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*, system_role, org_role')
        .eq('id', userId)
        .single();

      if (error) {
        console.error('Error fetching profile:', error);
        return null;
      }



      const { count: managedVendorsCount, error: managedVendorsError } = await supabase
        .from('vendor_managers')
        .select('id', { count: 'exact', head: true })
        .eq('manager_id', userId);

      if (managedVendorsError) {
        console.error('Error fetching managed vendors count:', managedVendorsError);
      }

      // Assurer la compatibilité : system_role prend priorité, sinon utiliser role
      const finalRole = data.system_role || data.role || 'client';
      
      return { 
        ...data, 
        has_vendor_management_access: (managedVendorsCount || 0) > 0,
        system_role: finalRole as SystemRole,
        role: finalRole as SystemRole // Pour compatibilité temporaire
      };
    } catch (error) {
      console.error('Error fetching profile:', error);
      return null;
    }
  };

  useEffect(() => {
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        
        if (session?.user) {
          // Fetch user profile with delay for trigger to complete
          const delay = event === 'SIGNED_UP' ? 2000 : 500;
          setTimeout(async () => {
            const profileData = await fetchProfile(session.user.id);
            setProfile(profileData);
            
            // Set Sentry user context
            if (profileData) {
              setUserContext({
                id: profileData.id,
                email: profileData.email || session.user.email,
                role: profileData.system_role,
              });
            }
            
            setLoading(false);
          }, delay);
        } else {
          setProfile(null);
          setUserContext(null); // Clear Sentry context on logout
          setLoading(false);
        }
      }
    );

    // Check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user) {
        fetchProfile(session.user.id).then((profileData) => {
          setProfile(profileData);
          
          // Set Sentry user context
          if (profileData) {
            setUserContext({
              id: profileData.id,
              email: profileData.email || session.user.email,
              role: profileData.system_role,
            });
          }
          
          setLoading(false);
        });
      } else {
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Cross-tab session sync: respond with current session and raw storage when requested
  useEffect(() => {
    const ch = new BroadcastChannel('auth-sync');
    ch.onmessage = async (event) => {
      if (event.data?.type === 'REQUEST_SESSION') {
        // Send raw localStorage value so the other tab can fully restore session (includes refresh_token)
        const storageKey = 'sb-apmnhnunfrcbydwysfez-auth-token';
        const storageValue = localStorage.getItem(storageKey);
        if (storageValue) {
          ch.postMessage({ type: 'SESSION_STORAGE', key: storageKey, value: storageValue });
        }

        // Also send the current session as a fallback
        const { data } = await supabase.auth.getSession();
        if (data.session) {
          ch.postMessage({ type: 'SESSION', session: data.session });
        } else {
          ch.postMessage({ type: 'NO_SESSION' });
        }
      }
    };
    return () => ch.close();
  }, []);

  // Global postMessage listener to respond to session requests from success tabs
  useEffect(() => {
    const handler = async (event: MessageEvent) => {
      try {
        if (event.origin !== window.location.origin) return;
        const data = (event.data || {}) as any;
        if (data.type === 'REQUEST_SESSION') {
          const storageKey = 'sb-apmnhnunfrcbydwysfez-auth-token';
          const storageValue = localStorage.getItem(storageKey);
          const src = event.source as Window | null;
          if (src && (src as any).postMessage) {
            if (storageValue) {
              src.postMessage({ type: 'SESSION_STORAGE', key: storageKey, value: storageValue }, event.origin);
            }
            const { data: s } = await supabase.auth.getSession();
            if (s.session?.access_token && s.session?.refresh_token) {
              src.postMessage(
                { type: 'SESSION_DATA', session: { access_token: s.session.access_token, refresh_token: s.session.refresh_token } },
                event.origin
              );
            } else {
              src.postMessage({ type: 'NO_SESSION' }, event.origin);
            }
          }
        }
      } catch {}
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);
  const signUp = async (email: string, password: string, fullName: string) => {
    try {
      // Vérifier si l'email existe déjà dans la table profiles
      const { data: existingProfile, error: checkError } = await supabase
        .from('profiles')
        .select('email')
        .eq('email', email)
        .maybeSingle();
      
      if (existingProfile) {
        toast({
          title: "Email déjà utilisé",
          description: "Cette adresse email est déjà utilisée. Veuillez vous connecter ou utiliser une autre adresse email.",
          variant: "destructive",
        });
        return { error: new Error("Email already exists") };
      }
      
      const redirectUrl = `${window.location.origin}/auth/confirmed`;
      
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            full_name: fullName,
          },
        },
      });

      if (error) {
        console.error('Supabase signup error FULL:', error);
        console.error('Error message:', error.message);
        console.error('Error code:', error.status);
        console.error('Error details:', JSON.stringify(error, null, 2));
        
        // Gestion spécifique pour email déjà utilisé
        let errorMessage = error.message;
        if (error.message.toLowerCase().includes('already registered') || 
            error.message.toLowerCase().includes('already exists') ||
            error.message.toLowerCase().includes('user already registered')) {
          errorMessage = "Cette adresse email est déjà utilisée. Veuillez vous connecter ou utiliser une autre adresse email.";
        } else if (error.message.toLowerCase().includes('invalid email')) {
          errorMessage = "Adresse email invalide.";
        } else if (error.message.toLowerCase().includes('password')) {
          errorMessage = "Le mot de passe doit contenir au moins 6 caractères.";
        }
        
        toast({
          title: "Erreur d'inscription",
          description: errorMessage,
          variant: "destructive",
        });
        return { error };
      }

      if (!data.user) {
        const err = new Error("User creation failed");
        toast({
          title: "Erreur d'inscription",
          description: "Impossible de créer le compte",
          variant: "destructive",
        });
        return { error: err };
      }

      // Wait 1 second for trigger to create profile
      await new Promise(resolve => setTimeout(resolve, 1000));


      toast({
        title: "Inscription réussie",
        description: "Bienvenue sur Nazandoo ! Vérifiez vos emails pour confirmer votre compte.",
      });

      return { error: null };
    } catch (error) {
      console.error('SignUp error:', error);
      return { error };
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        console.error('SignIn error:', error);
        
        // Messages d'erreur personnalisés en français
        let errorTitle = "Erreur de connexion";
        let errorMessage = error.message;
        
        if (error.message.toLowerCase().includes('invalid login credentials') || 
            error.message.toLowerCase().includes('invalid credentials')) {
          errorTitle = "Identifiants incorrects";
          errorMessage = "L'email ou le mot de passe est incorrect. Veuillez réessayer.";
        } else if (error.message.toLowerCase().includes('email not confirmed')) {
          errorTitle = "Email non confirmé";
          errorMessage = "Veuillez confirmer votre email avant de vous connecter. Vérifiez votre boîte de réception.";
        } else if (error.message.toLowerCase().includes('user not found')) {
          errorTitle = "Compte introuvable";
          errorMessage = "Aucun compte n'existe avec cette adresse email. Veuillez vous inscrire.";
        } else if (error.message.toLowerCase().includes('too many requests')) {
          errorTitle = "Trop de tentatives";
          errorMessage = "Trop de tentatives de connexion. Veuillez patienter quelques minutes.";
        } else if (error.message.toLowerCase().includes('invalid email')) {
          errorTitle = "Email invalide";
          errorMessage = "L'adresse email saisie n'est pas valide.";
        } else if (error.message.toLowerCase().includes('password')) {
          errorTitle = "Mot de passe incorrect";
          errorMessage = "Le mot de passe saisi est incorrect.";
        }
        
        toast({
          title: errorTitle,
          description: errorMessage,
          variant: "destructive",
        });
        return { error };
      }

      toast({
        title: "Connexion réussie",
        description: "Bienvenue sur Nazandoo!",
      });

      return { error: null };
    } catch (error) {
      console.error('SignIn exception:', error);
      toast({
        title: "Erreur de connexion",
        description: "Une erreur inattendue s'est produite. Veuillez réessayer.",
        variant: "destructive",
      });
      return { error };
    }
  };

  const signOut = async () => {
    try {
      await supabase.auth.signOut();
      toast({
        title: "Déconnexion",
        description: "À bientôt sur Nazandoo!",
      });
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const updateProfile = async (updates: Partial<Profile>) => {
    if (!user) return { error: new Error('No user logged in') };

    try {
      const { error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', user.id);

      if (error) {
        toast({
          title: "Erreur de mise à jour",
          description: error.message,
          variant: "destructive",
        });
        return { error };
      }

      // Refresh profile data
      const updatedProfile = await fetchProfile(user.id);
      setProfile(updatedProfile);

      toast({
        title: "Profil mis à jour",
        description: "Vos informations ont été mises à jour avec succès.",
      });

      return { error: null };
    } catch (error) {
      return { error };
    }
  };

  const updateEmail = async (newEmail: string) => {
    try {
      const { error } = await supabase.auth.updateUser({
        email: newEmail
      });

      if (error) {
        toast({
          title: "Erreur de mise à jour de l'email",
          description: error.message,
          variant: "destructive",
        });
        return { error };
      }

      toast({
        title: "Email mis à jour",
        description: "Vérifiez vos ancienne et nouvelle adresses email pour confirmer le changement.",
      });

      return { error: null };
    } catch (error) {
      return { error };
    }
  };

  const updatePassword = async (newPassword: string) => {
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) {
        toast({
          title: "Erreur de mise à jour du mot de passe",
          description: error.message,
          variant: "destructive",
        });
        return { error };
      }

      toast({
        title: "Mot de passe mis à jour",
        description: "Votre mot de passe a été modifié avec succès.",
      });

      return { error: null };
    } catch (error) {
      return { error };
    }
  };

  const value = {
    user,
    session,
    profile,
    loading,
    isEffectiveVendor,
    isAdmin,
    isSuperAdmin,
    isHR,
    isVendor,
    isRelay,
    isClient,
    signUp,
    signIn,
    signOut,
    updateProfile,
    updateEmail,
    updatePassword,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};